import React, { useCallback } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { Controller, useFormContext } from 'react-hook-form';
import ComponentProps from 'store/types/ComponentProps';
import { defaultInputProps, getRequiredValidationRule, getValidationErrorProps } from 'util/form';

const MEETING_DATETIME_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss';

interface Props extends Pick<TextFieldProps, 'size' | 'disabled'>, ComponentProps {
  fieldName: string;
  label: string;
  required?: boolean;
  minDate?: any;
  maxDate?: any;
  maxDateErrorMessage?: string;
  minDateErrorMessage?: string;
}

const MeetingDateTimeFormItem: React.FunctionComponent<Props> = ({
  fieldName,
  label,
  required = true,
  disabled,
  minDate = moment('1900-01-01'),
  maxDate = moment('2100-01-01'),
  maxDateErrorMessage,
  minDateErrorMessage,
}) => {
  const { control } = useFormContext();

  const validateField = useCallback(
    (inputValue: string | Moment | null) => {
      if (!inputValue) return;

      const inputDate =
        typeof inputValue === 'string' ? moment(inputValue, MEETING_DATETIME_FORMAT, true) : moment(inputValue);

      if (!inputDate.isValid()) return 'Please input valid date';
      if (minDate && inputDate.isBefore(moment(minDate)))
        return minDateErrorMessage || 'Date should not be before minimal date';
      if (maxDate && inputDate.isAfter(moment(maxDate)))
        return maxDateErrorMessage || 'Date should not be after maximal date';
    },
    [minDate, maxDate, maxDateErrorMessage, minDateErrorMessage]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Controller
        name={fieldName}
        control={control}
        rules={{ required: getRequiredValidationRule(label.toLowerCase(), required), validate: validateField }}
        render={({ field, fieldState: { error } }) => {
          const pickerValue = field.value ? moment(field.value, MEETING_DATETIME_FORMAT, true) : null;

          return (
            <DateTimePicker
              {...field}
              label={label}
              disabled={disabled}
              minDate={minDate}
              maxDate={maxDate}
              value={pickerValue}
              onChange={(newValue) => {
                const next =
                  newValue && moment(newValue).isValid() ? moment(newValue).format(MEETING_DATETIME_FORMAT) : null;
                field.onChange(next);
              }}
              renderInput={(params: TextFieldProps) => (
                <TextField {...params} {...defaultInputProps} {...getValidationErrorProps(error)} required={required} />
              )}
            />
          );
        }}
      />
    </LocalizationProvider>
  );
};

export default MeetingDateTimeFormItem;
